/**
 * ProductsTable
 *
 * Composes `DataTableProvider` + `DataTableContent` with the format-specific
 * column definitions, and mounts a tab-aware bulk-action overlay:
 * `BulkPublishBar` on Unpublished (GRAIN-1373), `BulkActivateDeactivateBar`
 * on Published (GRAIN-1521).
 *
 * ## Overlay
 *
 * Both bars are fully wired through the same pattern: confirm dialog →
 * single or bulk mutation based on `selectedIds.length`, with the bar's
 * state machine driven off the resulting mutation lifecycle.
 *
 * | tab         | `ids.length` | endpoint                               | bar transition                                  |
 * |-------------|--------------|----------------------------------------|-------------------------------------------------|
 * | Unpublished | 1            | `POST /api/products/:id/publish`       | `selected → loading → unmount` (sync, toast)    |
 * | Unpublished | 2+           | `POST /api/products/bulk_publish`      | `selected → loading → queued → auto-dismiss`    |
 * | Published   | 1            | `POST /api/products/:id/activate`      | `selected → loading → unmount` (sync, toast)    |
 * | Published   | 2+           | `POST /api/products/bulk_activate`     | `selected → loading → queued → auto-dismiss`    |
 * | Published   | 1            | `POST /api/products/:id/deactivate`    | `selected → loading → unmount` (sync, toast)    |
 * | Published   | 2+           | `POST /api/products/bulk_deactivate`   | `selected → loading → queued → auto-dismiss`    |
 *
 * The submit-time selection (ids + count) is snapshotted on confirm so the
 * loading/queued copy keeps its count even if the user un-ticks rows while
 * the bar is still on screen. Activate and deactivate share the same
 * `BulkActivateDeactivateBar` instance on the Published tab — the user can
 * only have one action in flight at a time, so the bar's loading / queued /
 * error state ORs together both mutations' lifecycles.
 *
 * ## Selection rules
 *
 * - **Selection threshold**: each bar shows whenever `selectedCount >= 1`.
 *   A single-row selection routes to the single endpoint; 2+ route to the
 *   async bulk endpoint. The row action menu remains as a shortcut for
 *   single-row actions that bypass the selection checkbox.
 * - **Reset on filter change**: the overlay clears row selection whenever
 *   `currentStatus` or the format selection changes — the underlying product
 *   set swaps out and any prior selection is stale. The formats array is
 *   passed in as a joined string (`formsKey`) so a fresh array identity on
 *   every render doesn't clear ticks as fast as the user makes them.
 * - **Rows are keyed by product id** (`getRowId`), not by position, so a
 *   selection key that survives a page / sort / search change can only
 *   resolve to nothing — never to whatever product now sits in that row.
 */

// React
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

// Libraries
import { AnimatePresence } from 'framer-motion';
import type { SortingState } from '@tanstack/react-table';

// Local components
import { ConfirmationDialog } from '@components/common/ConfirmationDialog';
import { DataTableContent, renderImageNameSkeletonCell } from '@components/DataTable/DataTableContent';
import { DataTableProvider, useDataTable } from '@components/DataTable/DataTableProvider';
import { ActivateCircleIcon } from '@components/icons/ActivateCircleIcon';
import { MinusCircleIcon } from '@components/icons/MinusCircleIcon';
import { PublishCircleIcon } from '@components/icons/PublishCircleIcon';
import {
  BulkActivateDeactivateBar,
  type BulkActivateDeactivateBarMode,
  type BulkActivateDeactivateSelectionState,
} from './BulkActivateDeactivateBar';
import { BulkPublishBar, type BulkPublishBarMode } from './BulkPublishBar';
import { createAllFormatColumns, createBuffetColumns, createMealBoxColumns } from '@components/Product/columns';
import { openActivityCentrePanel } from '@components/ActivityCentre/useActivityCentrePanel';

// Hooks and utilities
import { cn } from '@/lib/utils';
import { useActivateProduct } from '@hooks/products/useActivateProduct';
import { useDeactivateProduct } from '@hooks/products/useDeactivateProduct';
import { useDuplicateProduct } from '@hooks/products/useDuplicateProduct';
import { usePublishProduct } from '@hooks/products/usePublishProduct';
import { useBulkPublishProducts } from '@hooks/products/useBulkPublishProducts';
import { useBulkActivateProducts } from '@hooks/products/useBulkActivateProducts';
import { useBulkDeactivateProducts } from '@hooks/products/useBulkDeactivateProducts';
import { useTranslation } from '@hooks/useTranslation';
import { usePostHog } from '@posthog/react';
import { interpolateCount, pluralizeKey } from '@/react/utils/i18n';

// Types
import { MACROS_FORMATS, PORTION_FORMATS } from '@components/Product/types';
import type { Product, ProductPublishedStatus, ProductsTableProps } from '@components/Product/types';

interface PendingRow {
  id: number;
  name: string;
}

const EMPTY_PRODUCTS: never[] = [];
const EMPTY_SORTING: SortingState = [];

const BUFFET_FORMS = new Set<string>(PORTION_FORMATS);
const MEAL_BOX_FORMS = new Set<string>(MACROS_FORMATS);

// TanStack keys row selection by array index unless told otherwise, which makes
// a tick a position rather than a product. Key it by id instead.
const getProductRowId = (product: Product) => String(product.id);

/**
 * Height of a typical product row, fed to the virtualizer as its estimate for
 * rows it hasn't rendered yet. The 48px thumbnail sets the floor: a one-line
 * name is 21px at text-sm/leading-[1.5], and the format badge shares the
 * byline's line rather than taking one of its own, so with the 3px gap the
 * text stack is 45px — shorter than the thumbnail. Add TABLE_CELL_CLASSES'
 * `py-4` (12px a side on Grain's 3px spacing scale) and the row's 1px bottom
 * border: 48 + 24 + 1. Measured against real rows, which render at 73px, or
 * 91px where the name wraps to two lines. Those are the exception, and the
 * virtualizer measures each row for real once it renders.
 */
const PRODUCT_ROW_HEIGHT = 73;

interface OverlayProps {
  currentStatus: ProductPublishedStatus;
  /** `currentFormats.join(',')` — a stable string, so the reset effect below
   * doesn't re-fire on every render the way a raw array would. */
  formsKey: string;
}

type PendingAction = 'publish' | 'activate' | 'deactivate';

// Per-action dialog config. Title keys resolve to nested { one, other }
// objects for Rails-style pluralization — `pluralizeKey` picks the leaf
// by count so "Deactivate 1 product" reads correctly.
interface ConfirmConfig {
  titleKey: string;
  actionKey: string;
  variant: 'destructive' | 'success';
  iconColor: string;
  icon: (className: string) => ReactNode;
}

const CONFIRM_CONFIG: Record<PendingAction, ConfirmConfig> = {
  publish: {
    titleKey: 'bulk_bar.confirm_title',
    actionKey: 'bulk_bar.confirm_action',
    variant: 'success',
    iconColor: 'text-yield-positive',
    icon: (className) => <PublishCircleIcon className={className} />,
  },
  activate: {
    titleKey: 'bulk_bar.activate_confirm_title',
    actionKey: 'bulk_bar.activate_confirm_action',
    variant: 'success',
    iconColor: 'text-yield-positive',
    icon: (className) => <ActivateCircleIcon className={className} />,
  },
  deactivate: {
    titleKey: 'bulk_bar.deactivate_confirm_title',
    actionKey: 'bulk_bar.deactivate_confirm_action',
    variant: 'destructive',
    iconColor: 'text-chilli-red',
    icon: (className) => <MinusCircleIcon className={className} />,
  },
};

function ProductsBulkBarOverlay({ currentStatus, formsKey }: OverlayProps) {
  const posthog = usePostHog();
  const { t } = useTranslation();
  const { table } = useDataTable<Product>();
  const selectedRows = table.getSelectedRowModel().rows;
  const selectedCount = selectedRows.length;
  // Two short-circuited `every` passes — the mixed case bails on each pass
  // at the first counter-example, so the worst case is ~2 reads.
  const selectionState: BulkActivateDeactivateSelectionState =
    selectedRows.every((r) => r.original.active_status === 'active') ? 'all_active'
    : selectedRows.every((r) => r.original.active_status === 'inactive') ? 'all_inactive'
    : 'mixed';
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Snapshotted at confirm-dialog open. The dialog shows "Publish N products?"
  // and the running publish mutation uses these ids/count so later selection
  // tweaks don't change what actually gets submitted or displayed on the bar.
  const [pendingRows, setPendingRows] = useState<PendingRow[]>([]);
  // Which action the user just kicked off — drives the dialog copy and
  // routes `handleConfirm`.
  const [pendingAction, setPendingAction] = useState<PendingAction>('publish');
  const pendingCount = pendingRows.length;

  const {
    mutate: mutateSinglePublish,
    reset: resetSinglePublish,
    isPending: singlePublishPending,
    isSuccess: singlePublishSuccess,
  } = usePublishProduct();
  const {
    mutate: mutateBulkPublish,
    reset: resetBulkPublish,
    isPending: bulkPublishPending,
    isSuccess: bulkPublishSuccess,
    isError: bulkPublishError,
  } = useBulkPublishProducts();
  const {
    mutate: mutateSingleActivate,
    reset: resetSingleActivate,
    isPending: singleActivatePending,
    isSuccess: singleActivateSuccess,
  } = useActivateProduct();
  const {
    mutate: mutateBulkActivate,
    reset: resetBulkActivate,
    isPending: bulkActivatePending,
    isSuccess: bulkActivateSuccess,
    isError: bulkActivateError,
  } = useBulkActivateProducts();
  const {
    mutate: mutateSingleDeactivate,
    reset: resetSingleDeactivate,
    isPending: singleDeactivatePending,
    isSuccess: singleDeactivateSuccess,
  } = useDeactivateProduct();
  const {
    mutate: mutateBulkDeactivate,
    reset: resetBulkDeactivate,
    isPending: bulkDeactivatePending,
    isSuccess: bulkDeactivateSuccess,
    isError: bulkDeactivateError,
  } = useBulkDeactivateProducts();

  // When the tab or the format selection changes the underlying data swaps out
  // and any prior selection is stale. Also reset in-flight publish / activate /
  // deactivate mutations so a stale `queued` bar doesn't linger on the new tab.
  useEffect(() => {
    table.resetRowSelection();
    setConfirmOpen(false);
    resetSinglePublish();
    resetBulkPublish();
    resetSingleActivate();
    resetBulkActivate();
    resetSingleDeactivate();
    resetBulkDeactivate();
  }, [currentStatus, formsKey, table, resetSinglePublish, resetBulkPublish, resetSingleActivate, resetBulkActivate, resetSingleDeactivate, resetBulkDeactivate]);

  // When a synchronous single mutation resolves, clear the bar — the hook
  // already surfaces its own success/error toast.
  useEffect(() => {
    if (singlePublishSuccess) {
      resetSinglePublish();
      table.resetRowSelection();
    }
  }, [singlePublishSuccess, resetSinglePublish, table]);

  useEffect(() => {
    if (singleActivateSuccess) {
      resetSingleActivate();
      table.resetRowSelection();
    }
  }, [singleActivateSuccess, resetSingleActivate, table]);

  useEffect(() => {
    if (singleDeactivateSuccess) {
      resetSingleDeactivate();
      table.resetRowSelection();
    }
  }, [singleDeactivateSuccess, resetSingleDeactivate, table]);

  const openConfirm = useCallback(
    (action: PendingAction) => {
      const rows = table.getSelectedRowModel().rows.map((r) => ({
        id: r.original.id,
        name: r.original.name,
      }));
      setPendingRows(rows);
      setPendingAction(action);
      setConfirmOpen(true);
    },
    [table],
  );

  const handlePublishClick = useCallback(() => openConfirm('publish'), [openConfirm]);
  const handleActivateClick = useCallback(() => openConfirm('activate'), [openConfirm]);
  const handleDeactivateClick = useCallback(() => openConfirm('deactivate'), [openConfirm]);

  const handleConfirm = useCallback(() => {
    setConfirmOpen(false);
    if (pendingAction === 'publish') {
      if (pendingRows.length === 1) {
        mutateSinglePublish(pendingRows[0]);
      } else if (pendingRows.length > 1) {
        mutateBulkPublish({
          ids: pendingRows.map((r) => r.id),
          count: pendingRows.length,
        });
      }
      posthog.capture('product_bulk_published', { product_count: pendingRows.length });
      return;
    }
    if (pendingAction === 'activate') {
      if (pendingRows.length === 1) {
        mutateSingleActivate(pendingRows[0]);
      } else if (pendingRows.length > 1) {
        mutateBulkActivate({
          ids: pendingRows.map((r) => r.id),
          count: pendingRows.length,
        });
      }
      posthog.capture('product_bulk_activated', { product_count: pendingRows.length });
      return;
    }
    if (pendingAction === 'deactivate') {
      if (pendingRows.length === 1) {
        mutateSingleDeactivate(pendingRows[0]);
      } else if (pendingRows.length > 1) {
        mutateBulkDeactivate({
          ids: pendingRows.map((r) => r.id),
          count: pendingRows.length,
        });
      }
      posthog.capture('product_bulk_deactivated', { product_count: pendingRows.length });
    }
  }, [pendingAction, pendingRows, mutateSinglePublish, mutateBulkPublish, mutateSingleActivate, mutateBulkActivate, mutateSingleDeactivate, mutateBulkDeactivate, posthog]);

  const handlePublishBarDismiss = useCallback(() => {
    resetBulkPublish();
    table.resetRowSelection();
  }, [resetBulkPublish, table]);

  const handleActivateBarDismiss = useCallback(() => {
    resetBulkActivate();
    resetBulkDeactivate();
    table.resetRowSelection();
  }, [resetBulkActivate, resetBulkDeactivate, table]);

  const handleQueuedLinkClick = useCallback(() => openActivityCentrePanel('processing'), []);

  const publishMode: BulkPublishBarMode =
    (singlePublishPending || bulkPublishPending) ? 'loading'
    : bulkPublishSuccess ? 'queued'
    : bulkPublishError ? 'error'
    : 'selected';

  const activateMode: BulkActivateDeactivateBarMode =
    (singleActivatePending || bulkActivatePending || singleDeactivatePending || bulkDeactivatePending) ? 'loading'
    : (bulkActivateSuccess || bulkDeactivateSuccess) ? 'queued'
    : (bulkActivateError || bulkDeactivateError) ? 'error'
    : 'selected';

  // Keep each bar mounted for the full mutation lifecycle, even if the user
  // clears the row selection during loading / queued / error. Freeze the
  // count so the copy doesn't fall to zero mid-flight.
  const publishBarMounted =
    singlePublishPending || bulkPublishPending || bulkPublishSuccess || bulkPublishError;
  const publishBarCount = publishBarMounted ? pendingCount : selectedCount;
  const showPublishBar =
    (selectedCount >= 1 && currentStatus === 'unpublished') || publishBarMounted;

  const activateBarMounted =
    singleActivatePending || bulkActivatePending || bulkActivateSuccess || bulkActivateError
    || singleDeactivatePending || bulkDeactivatePending || bulkDeactivateSuccess || bulkDeactivateError;
  const activateBarCount = activateBarMounted ? pendingCount : selectedCount;
  const showActivateDeactivateBar =
    (selectedCount >= 1 && currentStatus === 'published') || activateBarMounted;

  const config = CONFIRM_CONFIG[pendingAction];

  return (
    <>
      <div className="pointer-events-none absolute inset-x-0 bottom-8 z-10 flex justify-center">
        <AnimatePresence>
          {showPublishBar && (
            <BulkPublishBar
              key="bulk-publish-bar"
              selectedCount={publishBarCount}
              mode={publishMode}
              onPublish={handlePublishClick}
              onDismissQueued={handlePublishBarDismiss}
              onDismissError={handlePublishBarDismiss}
              onAutoDismiss={handlePublishBarDismiss}
              onQueuedLinkClick={handleQueuedLinkClick}
            />
          )}
          {showActivateDeactivateBar && (
            <BulkActivateDeactivateBar
              key="bulk-activate-deactivate-bar"
              selectedCount={activateBarCount}
              mode={activateMode}
              selectionState={selectionState}
              onActivate={handleActivateClick}
              onDeactivate={handleDeactivateClick}
              onDismissQueued={handleActivateBarDismiss}
              onDismissError={handleActivateBarDismiss}
              onAutoDismiss={handleActivateBarDismiss}
              onQueuedLinkClick={handleQueuedLinkClick}
            />
          )}
        </AnimatePresence>
      </div>

      <ConfirmationDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={handleConfirm}
        variant={config.variant}
        icon={config.icon(`w-8 h-8 ${config.iconColor}`)}
        title={interpolateCount(t(pluralizeKey(config.titleKey, pendingCount)), pendingCount)}
        message={t('bulk_bar.confirm_message')}
        confirmLabel={interpolateCount(t(config.actionKey), pendingCount)}
        cancelLabel={t('bulk_bar.confirm_cancel')}
      />
    </>
  );
}

export function ProductsTable({
  products = EMPTY_PRODUCTS,
  currentFormats,
  currentStatus,
  isLoading = false,
  isFetching = false,
  onRowClick,
  sort,
  direction,
  onSortChange,
  canWrite = false,
  className,
}: ProductsTableProps) {
  const { t } = useTranslation();
  const { mutate: duplicate } = useDuplicateProduct();
  const { mutate: activate } = useActivateProduct();
  const { mutate: deactivate } = useDeactivateProduct();
  const { mutate: publish } = usePublishProduct();
  const onDuplicate = useCallback((product: Product) => duplicate(product.id), [duplicate]);
  const onActivate = useCallback((product: Product) => activate({ id: product.id, name: product.name }), [activate]);
  const onDeactivate = useCallback((product: Product) => deactivate({ id: product.id, name: product.name }), [deactivate]);
  const onPublish = useCallback((product: Product) => publish({ id: product.id, name: product.name }), [publish]);

  // A selection confined to one format family keeps that family's narrow
  // table; a mixed selection — or an empty one, which means every format —
  // gets the union so no row's distinguishing column is missing.
  const columns = useMemo(() => {
    const callbacks = { onDuplicate, onActivate, onDeactivate, onPublish, currentStatus, canWrite };
    const some = currentFormats.length > 0;
    if (some && currentFormats.every((f) => BUFFET_FORMS.has(f))) return createBuffetColumns(t, callbacks);
    if (some && currentFormats.every((f) => MEAL_BOX_FORMS.has(f))) return createMealBoxColumns(t, callbacks);
    return createAllFormatColumns(t, callbacks);
  }, [currentFormats, currentStatus, canWrite, t, onDuplicate, onActivate, onDeactivate, onPublish]);

  const formsKey = currentFormats.join(',');

  const sorting: SortingState = useMemo(
    () => (sort ? [{ id: sort, desc: direction === 'desc' }] : EMPTY_SORTING),
    [sort, direction],
  );

  const handleSortingChange = useCallback(
    (next: SortingState) => {
      if (next.length > 0) {
        onSortChange?.(next[0].id, next[0].desc ? 'desc' : 'asc');
      } else {
        onSortChange?.('', '');
      }
    },
    [onSortChange],
  );

  return (
    <div className={cn('relative flex flex-col', className)}>
      <DataTableProvider
        columns={columns}
        data={products}
        getRowId={getProductRowId}
        isLoading={isLoading}
        isFetching={isFetching}
        manualSorting={true}
        sorting={sorting}
        onSortingChange={handleSortingChange}
      >
        <DataTableContent
          renderSkeletonCell={renderImageNameSkeletonCell}
          skeletonRowClassName="animate-pulse"
          onRowClick={onRowClick}
          emptyTitle={t('table.empty')}
          estimatedRowHeight={PRODUCT_ROW_HEIGHT}
          className="[&_table]:min-w-[2400px]"
        />
        {/* RBAC: the bulk-action bar drives writes (publish/activate/deactivate).
            Read-only users have no selection checkbox, but gate the overlay too. */}
        {canWrite && (
          <ProductsBulkBarOverlay currentStatus={currentStatus} formsKey={formsKey} />
        )}
      </DataTableProvider>
    </div>
  );
}
